/**
 * The block library of custom components (owner feedback F21): how a building block is declared. One declaration
 * is the single source of truth for everything that knows the block:
 *
 *   - the typed props (Zod), with defaults, so a block always parses and the agent gets a JSON schema;
 *   - what each prop IS (a text with `{{ }}` tokens, an expression of an expected type, a data source, a state to
 *     edit, a call of an action, an icon …): the expression collector, the reference checks, the inspector and the
 *     renamer read this instead of a hand-written `switch` per block;
 *   - the palette entry (name, description, icon, presets) in English and German;
 *   - one example (props, children, sample data, state, actions): the live preview of the palette, what a new block
 *     starts with, and what the agent catalog shows.
 *
 * Pure data, browser-safe, no domain words. The React nodes that draw the blocks live in the web app
 * (`features/custom-components/runtime/blocks`), one per `type`.
 */
import { z } from 'zod';
import {
  ActionRef,
  ChoiceOption,
  Click,
  ExprText,
  SlotKey,
  Text,
  Tone,
  ValueFormat,
} from './custom-components-parts';

// -- labels -----------------------------------------------------------------------------------------------

export type Lbl = { en: string; de: string };
export const L = (en: string, de: string): Lbl => ({ en, de });
/** A label in a language (German when asked for German, English otherwise). */
export const lbl = (l: Lbl, locale: string): string => (locale.startsWith('de') ? l.de : l.en);

export const TONE_LABELS: Record<z.infer<typeof Tone>, Lbl> = {
  neutral: L('Neutral', 'Neutral'),
  accent: L('Accent', 'Akzent'),
  success: L('Green', 'Grün'),
  warning: L('Amber', 'Bernstein'),
  danger: L('Red', 'Rot'),
};
export const GAP_LABELS: Record<string, Lbl> = {
  none: L('None', 'Keiner'),
  xs: L('Tiny', 'Sehr klein'),
  sm: L('Small', 'Klein'),
  md: L('Medium', 'Mittel'),
  lg: L('Large', 'Groß'),
};
export const SIZE_LABELS: Record<string, Lbl> = {
  sm: L('Small', 'Klein'),
  md: L('Medium', 'Mittel'),
  lg: L('Large', 'Groß'),
};

// -- what a prop is ---------------------------------------------------------------------------------------

/** What an expression is expected to return (the place the value goes to). */
export type ExprExpect = 'any' | 'text' | 'number' | 'boolean' | 'date' | 'list';
export type SourceKindName = 'records' | 'record' | 'insight' | 'static';
export type StateKindName = 'text' | 'number' | 'boolean' | 'date' | 'choice' | 'record' | 'member' | 'list';

export type PropMeta =
  /** A text per language that may hold `{{ expression }}` tokens. `scope` names the record in scope (`row`, `entry`). */
  | { k: 'tpl'; scope?: string; soft?: boolean; long?: boolean; code?: 'html'; placeholder?: Lbl }
  | { k: 'expr'; expect: ExprExpect; scope?: string; placeholder?: Lbl }
  | { k: 'str'; mono?: boolean; code?: 'css'; placeholder?: Lbl }
  | { k: 'num'; min?: number; max?: number; step?: number; unit?: Lbl }
  | { k: 'bool' }
  | { k: 'pick'; options: readonly string[]; labels?: Record<string, Lbl>; seg?: boolean }
  | { k: 'icon' }
  | { k: 'source'; kinds: readonly SourceKindName[]; opt?: boolean }
  | { k: 'state'; types: readonly StateKindName[] }
  /** A call of an action; `req` = a missing call is a problem. */
  | { k: 'call'; req?: boolean; scope?: string; extras?: Record<string, 'text' | 'number' | 'boolean' | 'date' | 'any'> }
  | { k: 'click'; scope?: string }
  | { k: 'options' }
  | { k: 'format' }
  | { k: 'object' }
  /** A thing of the platform picked from its own list (a meeting type). */
  | { k: 'ref'; ref: 'meeting_type' }
  /** A field key of the object of the source named by prop `from` (`title` and `id` are always there). */
  | { k: 'field'; from: string; placeholder?: Lbl }
  | { k: 'items'; shape: Shape; titleKey?: string; min?: number; max?: number; addLabel?: Lbl };

export type Prop<Z = z.ZodType> = {
  zod: Z;
  meta: PropMeta;
  label: Lbl;
  hint?: Lbl;
  /** Shown in the inspector only when another prop has one of these values. */
  when?: { prop: string; in: readonly unknown[] };
  /** Goes into the "More" section of the inspector. */
  advanced?: boolean;
};
export type Shape = Record<string, Prop>;

type Common = { hint?: Lbl; when?: Prop['when']; advanced?: boolean };
const mk = <Z>(zod: Z, meta: PropMeta, en: string, de: string, o: Common = {}): Prop<Z> => ({
  zod,
  meta,
  label: L(en, de),
  ...(o.hint ? { hint: o.hint } : {}),
  ...(o.when ? { when: o.when } : {}),
  ...(o.advanced ? { advanced: true } : {}),
});

const TplText = (max: number) => Text(max);
/** `opt: true` makes a prop optional (no value), otherwise it has a default. */
type Opt<O, Z extends z.core.SomeType> = O extends { opt: true } ? z.ZodOptional<Z> : z.ZodDefault<Z>;
type TextZ = ReturnType<typeof Text>;
/** A localized text prop. */
const tpl = <const O extends Common & { opt?: boolean; def?: string; scope?: string; soft?: boolean; max?: number; long?: boolean; code?: 'html'; placeholder?: Lbl }>(en: string, de: string, o: O = {} as O) => {
  const base = TplText(o.max ?? 500);
  return mk<Opt<O, TextZ>>(
    (o.opt ? base.optional() : base.default(o.def ?? '')) as Opt<O, TextZ>,
    { k: 'tpl', ...(o.scope ? { scope: o.scope } : {}), ...(o.soft ? { soft: true } : {}), ...(o.code ? { code: o.code } : {}), ...(o.long ? { long: true } : {}), ...(o.placeholder ? { placeholder: o.placeholder } : {}) },
    en,
    de,
    o,
  );
};

export const P = {
  /** A text per language with `{{ }}` tokens; `opt`: may be absent. */
  tpl,
  expr: <const O extends Common & { expect?: ExprExpect; opt?: boolean; def?: string; scope?: string; placeholder?: Lbl }>(en: string, de: string, o: O = {} as O) =>
    mk<Opt<O, typeof ExprText>>(
      (o.opt ? ExprText.optional() : ExprText.default(o.def ?? '')) as Opt<O, typeof ExprText>,
      { k: 'expr', expect: o.expect ?? 'any', ...(o.scope ? { scope: o.scope } : {}), ...(o.placeholder ? { placeholder: o.placeholder } : {}) },
      en,
      de,
      o,
    ),
  /** A plain string (an address, a code, a colour name …). */
  str: <const O extends Common & { opt?: boolean; def?: string; max?: number; mono?: boolean; code?: 'css'; pattern?: RegExp; placeholder?: Lbl }>(en: string, de: string, o: O = {} as O) => {
    let z0 = z.string().max(o.max ?? 300);
    if (o.pattern) z0 = z0.regex(o.pattern);
    return mk<Opt<O, z.ZodString>>((o.opt ? z0.optional() : z0.default(o.def ?? '')) as Opt<O, z.ZodString>, { k: 'str', ...(o.mono ? { mono: true } : {}), ...(o.code ? { code: o.code } : {}), ...(o.placeholder ? { placeholder: o.placeholder } : {}) }, en, de, o);
  },
  num: <const O extends Common & { def?: number; min?: number; max?: number; step?: number; int?: boolean; opt?: boolean; unit?: Lbl }>(en: string, de: string, o: O = {} as O) => {
    let z0 = o.int ? z.number().int() : z.number();
    if (o.min !== undefined) z0 = z0.min(o.min);
    if (o.max !== undefined) z0 = z0.max(o.max);
    return mk<Opt<O, z.ZodNumber>>(
      (o.opt ? z0.optional() : z0.default(o.def ?? o.min ?? 0)) as Opt<O, z.ZodNumber>,
      {
        k: 'num',
        ...(o.min !== undefined ? { min: o.min } : {}),
        ...(o.max !== undefined ? { max: o.max } : {}),
        ...(o.step !== undefined ? { step: o.step } : {}),
        ...(o.unit ? { unit: o.unit } : {}),
      },
      en,
      de,
      o,
    );
  },
  bool: (en: string, de: string, def = false, o: Common = {}) => mk(z.boolean().default(def), { k: 'bool' }, en, de, o),
  pick: <const V extends readonly [string, ...string[]]>(
    en: string,
    de: string,
    options: V,
    def: V[number],
    o: Common & { labels?: Record<string, Lbl>; seg?: boolean } = {},
  ) =>
    mk(
      z.enum(options).default(def),
      { k: 'pick', options, ...(o.labels ? { labels: o.labels } : {}), ...(o.seg ? { seg: true } : {}) },
      en,
      de,
      o,
    ),
  tone: (en: string, de: string, def: z.infer<typeof Tone> = 'neutral', o: Common = {}) =>
    mk(Tone.default(def), { k: 'pick', options: Tone.options, labels: TONE_LABELS }, en, de, o),
  icon: (o: Common & { en?: string; de?: string } = {}) => mk(z.string().max(40).optional(), { k: 'icon' }, o.en ?? 'Icon', o.de ?? 'Symbol', o),
  source: (en: string, de: string, kinds: readonly SourceKindName[], o: Common & { opt?: boolean } = {}) =>
    mk(z.string().max(40).default(''), { k: 'source', kinds, ...(o.opt ? { opt: true } : {}) }, en, de, o),
  state: <const O extends Common & { opt?: boolean }>(en: string, de: string, types: readonly StateKindName[], o: O = {} as O) =>
    mk<Opt<O, z.ZodString>>((o.opt ? z.string().max(40).optional() : z.string().max(40).default('')) as Opt<O, z.ZodString>, { k: 'state', types }, en, de, o),
  call: (en: string, de: string, o: Common & { req?: boolean; scope?: string; extras?: Record<string, 'text' | 'number' | 'boolean' | 'date' | 'any'> } = {}) =>
    mk(
      ActionRef.optional(),
      { k: 'call', ...(o.req ? { req: true } : {}), ...(o.scope ? { scope: o.scope } : {}), ...(o.extras ? { extras: o.extras } : {}) },
      en,
      de,
      o,
    ),
  click: (en: string, de: string, o: Common & { scope?: string } = {}) =>
    mk(Click.optional(), { k: 'click', ...(o.scope ? { scope: o.scope } : {}) }, en, de, o),
  options: (en: string, de: string, o: Common & { max?: number; def?: Array<z.infer<typeof ChoiceOption>> } = {}) =>
    mk(z.array(ChoiceOption).max(o.max ?? 30).default(o.def ?? []), { k: 'options' }, en, de, o),
  format: (en = 'Format', de = 'Format', o: Common = {}) => mk(ValueFormat.default({ kind: 'auto' }), { k: 'format' }, en, de, o),
  object: <const O extends Common & { opt?: boolean }>(en: string, de: string, o: O = {} as O) =>
    mk<Opt<O, z.ZodString>>((o.opt ? z.string().max(63).optional() : z.string().max(63).default('')) as Opt<O, z.ZodString>, { k: 'object' }, en, de, o),
  ref: (en: string, de: string, ref: 'meeting_type', o: Common = {}) => mk(z.string().max(80).default(''), { k: 'ref', ref }, en, de, o),
  field: <const O extends Common & { opt?: boolean; def?: string; placeholder?: Lbl }>(en: string, de: string, from: string, o: O = {} as O) =>
    mk<Opt<O, z.ZodString>>(
      (o.opt ? z.string().max(63).optional() : z.string().max(63).default(o.def ?? '')) as Opt<O, z.ZodString>,
      { k: 'field', from, ...(o.placeholder ? { placeholder: o.placeholder } : {}) },
      en,
      de,
      o,
    ),
  items: <S extends Shape>(
    en: string,
    de: string,
    shape: S,
    o: Common & { def?: Array<Record<string, unknown>>; min?: number; max?: number; titleKey?: string; addLabel?: Lbl } = {},
  ) => {
    const item = shapeSchema(shape);
    let arr = z.array(item).max(o.max ?? 30);
    if (o.min) arr = arr.min(o.min);
    return mk(
      arr.default((o.def ?? []) as never),
      {
        k: 'items',
        shape,
        ...(o.titleKey ? { titleKey: o.titleKey } : {}),
        ...(o.min !== undefined ? { min: o.min } : {}),
        ...(o.max !== undefined ? { max: o.max } : {}),
        ...(o.addLabel ? { addLabel: o.addLabel } : {}),
      },
      en,
      de,
      o,
    );
  },
};

type ZodShapeOf<S extends Shape> = { [K in keyof S]: Extract<S[K]['zod'], z.core.$ZodType> };
/** The strict object schema of a shape of props. */
export function shapeSchema<S extends Shape>(shape: S): z.ZodObject<ZodShapeOf<S>, z.core.$strict> {
  const out: Record<string, z.ZodType> = {};
  for (const [k, p] of Object.entries(shape)) out[k] = p.zod as z.ZodType;
  return z.strictObject(out as unknown as ZodShapeOf<S>);
}

// -- blocks -----------------------------------------------------------------------------------------------

export const BLOCK_FAMILIES = ['layout', 'content', 'data', 'chart', 'input', 'action', 'platform', 'markup'] as const;
export type BlockFamily = (typeof BLOCK_FAMILIES)[number];

export const FAMILY_LABELS: Record<BlockFamily, Lbl> = {
  layout: L('Layout', 'Layout'),
  content: L('Content', 'Inhalt'),
  data: L('Data display', 'Datenanzeige'),
  chart: L('Charts', 'Diagramme'),
  input: L('Inputs', 'Eingaben'),
  action: L('Actions', 'Aktionen'),
  platform: L('Connected', 'Verbunden'),
  markup: L('Custom HTML', 'Eigenes HTML'),
};

/** A row of a static (sample) data source. */
export type StaticRow = Record<string, string | number | boolean | null>;

/** What a block starts with: the node, what it needs next to it (sample data, state, actions) and what it shows. */
export type BlockExample = {
  props?: Record<string, unknown>;
  children?: ExampleNode[];
  /** Static data sources the example reads (ids are renamed when they are taken). */
  data?: Array<{ id: string; fields: Array<{ key: string; type: 'text' | 'number' | 'date' | 'boolean' }>; rows: StaticRow[] }>;
  state?: Array<{ key: string; type: Record<string, unknown>; initial?: string }>;
  actions?: Array<{ id: string; name: Lbl; params?: Array<{ key: string; type: Record<string, unknown> }>; steps: Array<Record<string, unknown>> }>;
  /** Height in px of the palette preview (default: natural). */
  previewHeight?: number;
};
export type ExampleNode = { type: string; props?: Record<string, unknown>; children?: ExampleNode[] };

export type BlockPreset = { id: string; name: Lbl; desc?: Lbl; icon?: string; props: Record<string, unknown>; keywords?: readonly string[] };

export type ChildRule = 'none' | 'many' | { only: readonly string[] };

export type BlockDef<S extends Shape = Shape> = {
  name: Lbl;
  desc: Lbl;
  family: BlockFamily;
  /** An icon name of the design system. */
  icon: string;
  keywords?: readonly string[];
  /** One English sentence for the agent catalog: what it is for and what to bind. */
  summary: string;
  children?: ChildRule;
  /** The block can only sit directly inside one of these (an accordion's panel, a wizard's step). */
  parents?: readonly string[];
  /** Scope name (`row`) → prop that names the source whose rows it reads. */
  scopes?: Record<string, string>;
  props: S;
  presets?: readonly BlockPreset[];
  example: BlockExample;
};

type DefineOptions<S extends Shape> = Omit<BlockDef<S>, 'props'> & { props: S };

export type DefinedBlock<S extends Shape = Shape> = BlockDef<S> & { schema: z.ZodObject<ZodShapeOf<S>, z.core.$strict> };

/** Declare a block: its props become the strict schema. */
export function defineBlock<S extends Shape>(spec: DefineOptions<S>): DefinedBlock<S> {
  return { ...spec, schema: shapeSchema(spec.props) };
}

/** `{ prop: input type }` of a defined block (what the agent and presets may set). */
export type BlockInput<B extends DefinedBlock> = z.input<B['schema']>;
export type BlockProps<B extends DefinedBlock> = z.output<B['schema']>;

// -- walking the props of a block ------------------------------------------------------------------------

export type PropVisit = {
  /** Dot path inside the props (`items.0.label`). */
  path: string;
  prop: Prop;
  value: unknown;
};

/** Every prop of a block's props, nested items included, with its value (the value may be undefined). */
export function walkProps(shape: Shape, props: Record<string, unknown>, visit: (v: PropVisit) => void, prefix = ''): void {
  for (const [key, prop] of Object.entries(shape)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = props[key];
    visit({ path, prop, value });
    if (prop.meta.k === 'items' && Array.isArray(value))
      value.forEach((item, i) => {
        if (item && typeof item === 'object') walkProps(prop.meta.k === 'items' ? prop.meta.shape : {}, item as Record<string, unknown>, visit, `${path}.${i}`);
      });
  }
}

/**
 * Whether a prop shows in the inspector given the other values of its object. `__parent` stands for the type of the
 * parent node; the value `__set` matches any value that is not empty.
 */
export function propShown(prop: Prop, values: Record<string, unknown>): boolean {
  if (!prop.when) return true;
  const v = values[prop.when.prop];
  if (prop.when.in.includes('__set')) return !isEmptyValue(v);
  return prop.when.in.includes(v);
}

/** Whether a prop value says nothing (undefined, an empty text or a text map with no text). */
export function isEmptyValue(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (typeof v === 'object' && !Array.isArray(v)) return Object.values(v as Record<string, unknown>).every((x) => typeof x === 'string' && x.trim() === '');
  return false;
}

export { SlotKey as BlockSlotKey };
